import React, { useState } from 'react';
import type { ImageListType } from 'react-images-uploading';
import { ImageUploader } from './ImageUploader';
import Select from 'react-select';
import type { ValueType } from 'react-select';
import Button, { ButtonType } from '../../shared/components/core-ui/button/button.component';
import styles from '../furnishingContainer.module.scss';
import type { PortfolioProject } from './portfolio.types';
import { emptyPortfolioForm } from './portfolio.types';
import type { NumberOfRoomsOption } from '../furnishing.service';
import { createPortfolio } from '../furnishing.service';
import { useFurnishingUnitTypes } from '../FurnishingUnitTypesContext';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';

interface PortfolioFormProps {
  packages: { value: string; label: string }[];
  models: { value: string; label: string }[];
  onProjectAdded: (project: PortfolioProject) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const generateId = () => `portfolio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const PortfolioForm: React.FC<PortfolioFormProps> = ({
  packages,
  onProjectAdded,
  models
}) => {
  const { unitTypeOptions, loadingUnitTypes, homeTypeOptions, loadingHomeTypes } =
    useFurnishingUnitTypes();
  const [formState, setFormState] = useState(emptyPortfolioForm);
  const [overviewImageList, setOverviewImageList] = useState<
    Array<{ dataURL?: string; file?: File }>
  >([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOtherSelected, setIsOtherSelected] = useState(false);

  const handleTextChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: Number(value) || 0 }));
  };

  const handleSelectChange = (
    name: string,
    selectedOption: ValueType<SelectOption, false>
  ): void => {
    if (selectedOption) {
      setFormState((prev) => ({ ...prev, [name]: (selectedOption as SelectOption).value }));
    }
  };
  const handleSelectHomeTypeChange = (
    name: string,
    selectedOption: ValueType<SelectOption, false>
  ): void => {
    if (selectedOption) {
      setFormState((prev) => ({ ...prev, [name]: (selectedOption as SelectOption).label }));
    }
  };

  const handleNumberSelectChange = (
    name: string,
    selectedOption: ValueType<NumberOfRoomsOption, false>
  ): void => {
    if (selectedOption) {
      setFormState((prev) => ({ ...prev, [name]: (selectedOption as NumberOfRoomsOption).value }));
    }
  };

  const handleOverviewImageChange = (imageList: ImageListType) => {
    setOverviewImageList(imageList as Array<{ dataURL?: string; file?: File }>);
    if (imageList[0]?.dataURL) {
      setFormState((prev) => ({ ...prev, overviewImage: imageList[0].dataURL || '' }));
    }
  };

  const handleSubmit = async () => {
    const {
      projectName,
      projectNameAr,
      description,
      descriptionAr,
      location,
      priceMin,
      priceMax,
      homeType,
      packageId,
      overviewImage,
      timeline,
      modelId,
      otherModel,
      order
    } = formState;

    if (
      !projectName ||
      !projectNameAr ||
      !description ||
      !descriptionAr ||
      !location ||
      !homeType ||
      !packageId ||
      !timeline ||
      !overviewImage ||
      (!modelId && !otherModel)
    ) {
      setError('Please fill in all required fields and upload an overview image.');
      return;
    }

    if (priceMin > priceMax) {
      setError('Minimum price cannot be greater than maximum price.');
      return;
    }

    const imageFile = overviewImageList[0]?.file;
    if (!imageFile) {
      setError('Please upload an overview image.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const portfolioData = {
        name: projectName,
        nameAr: projectNameAr,
        location,
        year: formState.year,
        timeline,
        homeType,
        packageId: packageId,
        numberOfBedrooms: formState.numberOfBedrooms,
        minPrice: priceMin,
        maxPrice: priceMax,
        description,
        descriptionAr,
        asset: imageFile,
        modelId: modelId || undefined,
        otherModel: otherModel || undefined,
        order: order
      };

      const result = await createPortfolio(portfolioData);
      if (isErrorInfo(result)) {
        setError(result.errorMessage || 'Failed to create portfolio. Please try again.');
        return;
      }

      const newProject: PortfolioProject = {
        id: result?.id || generateId(),
        projectName,
        projectNameAr,
        description,
        descriptionAr,
        location,
        priceMin,
        priceMax,
        homeType,
        packageId,
        modelId: modelId || undefined,
        otherModel: otherModel || undefined,
        numberOfBedrooms: formState.numberOfBedrooms,
        timeline: formState.timeline,
        year: formState.year,
        overviewImage: result?.image || overviewImage,
        rooms: [],
        order: result?.order || order
      };

      onProjectAdded(newProject);
      resetForm();
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormState(emptyPortfolioForm);
    setOverviewImageList([]);
    setError('');
    setIsOtherSelected(false);
  };

  return (
    <div className={styles['furnishing-management__form-card']}>
      <div className={styles['furnishing-management__section-header']}>
        <h2>Add Portfolio Project</h2>
        <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
          Create the main project details first, then add rooms using the form below.
        </p>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div className={styles['furnishing-management__form-grid']}>
          <label className={styles['furnishing-management__input']}>
            <span>Project Name *</span>
            <input
              type="text"
              name="projectName"
              placeholder="Enter project name"
              value={formState.projectName}
              onChange={handleTextChange}
            />
          </label>
          <label className={styles['furnishing-management__input']}>
            <span>Project Name (Arabic) *</span>
            <input
              type="text"
              name="projectNameAr"
              placeholder="أدخل اسم المشروع"
              dir="rtl"
              value={formState.projectNameAr}
              onChange={handleTextChange}
            />
          </label>
          <label className={styles['furnishing-management__input']}>
            <span>Location *</span>
            <input
              type="text"
              name="location"
              placeholder="e.g., Marina Marassi, North Coast"
              value={formState.location}
              onChange={handleTextChange}
            />
          </label>
          <label className={styles['furnishing-management__input']}>
            <span>Year</span>
            <input
              type="number"
              name="year"
              placeholder="e.g., 2025"
              min={2000}
              max={2100}
              value={formState.year || ''}
              onChange={handleNumberChange}
            />
          </label>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div className={styles['furnishing-management__form-grid']}>
          <div className={styles['furnishing-management__input']}>
            <span>Home Type *</span>
            <div className={styles['furnishing-management__select']}>
              <Select
                classNamePrefix="react-select"
                placeholder="Select home type..."
                options={homeTypeOptions}
                isLoading={loadingHomeTypes}
                value={homeTypeOptions.find((opt) => opt.label === formState.homeType) || null}
                onChange={(option) => handleSelectHomeTypeChange('homeType', option)}
              />
            </div>
          </div>
          <div className={styles['furnishing-management__input']}>
            <span>Package Used *</span>
            <div className={styles['furnishing-management__select']}>
              <Select
                classNamePrefix="react-select"
                placeholder="Select package..."
                options={packages}
                value={packages.find((opt) => opt.value === formState.packageId) || null}
                onChange={(option) => handleSelectChange('packageId', option)}
              />
            </div>
          </div>
          <div className={styles['furnishing-management__input']}>
            <span>Style Used *</span>
            <div className={styles['furnishing-management__select']}>
              <Select
                classNamePrefix="react-select"
                placeholder="Select style..."
                options={[...models, { value: 'other', label: 'Other' }]}
                value={
                  formState.modelId
                    ? models.find((opt) => opt.value === formState.modelId) || null
                    : formState.otherModel
                    ? { value: 'other', label: 'Other' }
                    : null
                }
                onChange={(option) => {
                  if (option?.value === 'other') {
                    setIsOtherSelected(true);
                    setFormState((prev) => ({ ...prev, modelId: '', otherModel: '' }));
                  } else {
                    setIsOtherSelected(false);
                    handleSelectChange('modelId', option);
                    setFormState((prev) => ({ ...prev, otherModel: '' }));
                  }
                }}
              />
            </div>
          </div>
          {isOtherSelected && (
            <label className={styles['furnishing-management__input']}>
              <span>Custom Style Name *</span>

              <input
                type="text"
                name="otherModel"
                placeholder="Enter custom style name"
                value={formState.otherModel}
                onChange={handleTextChange}
              />
            </label>
          )}
          <div className={styles['furnishing-management__input']}>
            <span>Number of Bedrooms</span>
            <div className={styles['furnishing-management__select']}>
              <Select
                classNamePrefix="react-select"
                placeholder="Select unit type..."
                options={unitTypeOptions}
                isLoading={loadingUnitTypes}
                value={
                  unitTypeOptions.find((opt) => opt.value === String(formState.numberOfBedrooms)) ||
                  null
                }
                onChange={(option) => handleNumberSelectChange('numberOfBedrooms', option)}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div className={styles['furnishing-management__form-grid']}>
          <label className={styles['furnishing-management__input']}>
            <span>Price Min (EGP)</span>
            <input
              type="number"
              name="priceMin"
              placeholder="e.g., 227000"
              value={formState.priceMin || ''}
              onChange={handleNumberChange}
            />
          </label>
          <label className={styles['furnishing-management__input']}>
            <span>Price Max (EGP)</span>
            <input
              type="number"
              name="priceMax"
              placeholder="e.g., 230000"
              value={formState.priceMax || ''}
              onChange={handleNumberChange}
            />
          </label>
          <label className={styles['furnishing-management__input']}>
            <span>Timeline (Weeks)</span>
            <input
              type="number"
              name="timeline"
              placeholder="e.g., 3"
              min={1}
              value={formState.timeline || ''}
              onChange={handleNumberChange}
            />
          </label>
          <label className={styles['furnishing-management__input']}>
            <span>Order</span>
            <input
              type="number"
              name="order"
              placeholder="e.g., 1"
              min={0}
              value={formState.order ?? ''}
              onChange={handleNumberChange}
            />
          </label>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1.5rem', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className={styles['furnishing-management__input']} style={{ flex: '1 1 300px' }}>
              <span>Overview Image *</span>
              <span className={styles['furnishing-management__helper-text']}>
                Upload a square image (550 x 550 px) for the sharpest display.
              </span>
              <ImageUploader
                images={overviewImageList}
                onImageChange={handleOverviewImageChange}
                maxNumber={1}
                altText="Overview"
              />
            </div>
            <label className={styles['furnishing-management__input']} style={{ flex: '2 1 400px' }}>
              <span>Description *</span>
              <textarea
                name="description"
                placeholder="Describe the property, style, and key features..."
                rows={6}
                value={formState.description}
                onChange={handleTextChange}
                style={{ height: '100%' }}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <label className={styles['furnishing-management__input']} style={{ flex: '1 1 100%' }}>
              <span>Description (Arabic) *</span>
              <textarea
                name="descriptionAr"
                placeholder="وصف العقار والأسلوب والميزات الرئيسية..."
                dir="rtl"
                rows={4}
                value={formState.descriptionAr}
                onChange={handleTextChange}
              />
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '1rem',
            background: '#ffebee',
            color: '#c62828',
            borderRadius: '8px',
            marginTop: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: '500'
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>⚠️</span> {error}
        </div>
      )}

      <div
        className={styles['furnishing-management__actions']}
        style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #eee' }}
      >
        <Button type={ButtonType.gray} onClick={resetForm} disabled={isSubmitting}>
          Reset Form
        </Button>
        <Button onClick={handleSubmit} type={ButtonType.primary} disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create Project'}
        </Button>
      </div>
    </div>
  );
};

export default PortfolioForm;
